import { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { Skeleton } from 'primereact/skeleton';
import AuthContext from '../../../../auth/AuthContext';
import AlertService from '../../../../services/AlertService';
import AddDocTypeModal from "./AddDocTypeModal";
import DocTypeCard from "./DocTypeCard";
import DocumentTypeService from "../../../../services/DocumentTypeService";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";


const DocumentTypes = () => {
  const [fullDocTypes, setFullDocTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [docTypeToDelete, setDocTypeToDelete] = useState(null);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  useEffect(() => {
    const documentAccess = authContext.getPermissions("documenttypes") || [];
    if (documentAccess.filter(f => f.access.includes(1)).length === 0 || authContext.onGoingValidations) {
      navigate("/");
    }
    setPermissions({
      showAdd: documentAccess.some(f => f.access.includes(2)),
      showEdit: documentAccess.some(f => f.access.includes(3)),
      showDelete: documentAccess.some(f => f.access.includes(4)),
    });
  }, [navigate, authContext]);

const loadDocumentTypes = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await DocumentTypeService.GetAll(
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setFullDocTypes(Array.isArray(data.data) ? data.data : []);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadDocumentTypes(controller.signal); 
    return () => controller.abort(); 
  }, [loadDocumentTypes]);

  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedDocType(null);
    setShowModal(true);
  };

  const handleClick = (title, id) => {
    const slug = title.toLowerCase().replace(/\s+/g, "-");
    navigate(`/document-dashboard/${slug}`, { state: { title, value: id } });

  };

  const confirmDelete = (document) => {
    setDocTypeToDelete(document);
    setShowDeleteDialog(true);
  };

  const performDelete = async () => {
    setShowDeleteDialog(false);
    if (docTypeToDelete) {
      const result = await DocumentTypeService.Remove(docTypeToDelete.id, authContext);
      if (result.isSuccess) {
        loadDocumentTypes();
        AlertService.success("Document Type deleted successfully!");
      } else {
        AlertService.error(result.errorMessage);
      }
    }
  };


  return (
    <div className="container-fluid mt-3">
      {permissions.showAdd && (
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary py-2" onClick={handleAddClick}>
            <FaPlus className="pb-1 me-1" />
            Add New Document Folder
          </button>
        </div>
      )}

      <div className="row g-4 mb-4">
        {loading ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <div key={idx} className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3">
              <div className="card border-0 shadow-sm p-3 h-100 d-flex flex-row align-items-center gap-3">
                <Skeleton shape="circle" size="3rem" className="flex-shrink-0" />
                <div className="flex-grow-1">
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              </div>
            </div>
          ))
        ) : fullDocTypes.length > 0 ? (
          fullDocTypes.map(document => (
            <div
              key={document.id}
              className="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3"
              onClick={() => handleClick(document.documentType, document.id)}
              style={{ cursor: "pointer" }}
            >
              <DocTypeCard
                id={document.id}
                title={document.documentType}
                onEdit={permissions.showEdit ? () => {
                  setSelectedDocType(document);
                  setIsEditMode(true);
                  setShowModal(true);
                } : null}
                onDelete={permissions.showDelete ? () => confirmDelete(document) : null}
              />
            </div>
          ))
        ) : (
          <div className="col-12 text-center">No Document Folders available...</div>
        )}
      </div>

      <AddDocTypeModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        isEditMode={isEditMode}
        initialDocType={selectedDocType}
        reloadList={loadDocumentTypes}
      />

      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={performDelete}
        title={
          <>
            Are you sure you want to delete the document folder{" "}
            <strong>{docTypeToDelete?.documentType}</strong>?
          </>
        }
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default DocumentTypes;
